// src/money.ts
var CURRENCIES = /* @__PURE__ */ new Set(["GBP", "USD"]);
var Money = class _Money {
  amount;
  currency;
  constructor(amount, currency) {
    if (!Number.isFinite(amount)) {
      throw new TypeError(`Money.amount must be a finite number, got ${amount}`);
    }
    if (!Number.isInteger(amount)) {
      throw new TypeError(
        `Money.amount must be an integer (minor units), got ${amount}. Convert float currency values with Math.round(value * 100).`
      );
    }
    if (!CURRENCIES.has(currency)) {
      throw new TypeError(`Money.currency must be one of GBP, USD; got ${String(currency)}`);
    }
    this.amount = amount;
    this.currency = currency;
  }
  static zero(currency) {
    return new _Money(0, currency);
  }
  add(other) {
    this.assertSameCurrency(other, "add");
    return new _Money(this.amount + other.amount, this.currency);
  }
  sub(other) {
    this.assertSameCurrency(other, "sub");
    return new _Money(this.amount - other.amount, this.currency);
  }
  /**
   * Multiply by a scalar, rounding half-to-even (banker's rounding) back to
   * integer minor units. Banker's rounding is the default for IEEE 754 and
   * avoids the systematic bias of half-away-from-zero for large aggregates.
   */
  mul(scalar) {
    if (!Number.isFinite(scalar)) {
      throw new TypeError(`Money.mul scalar must be a finite number, got ${scalar}`);
    }
    return new _Money(bankersRound(this.amount * scalar), this.currency);
  }
  /**
   * Divide by a scalar with banker's rounding.
   */
  div(scalar) {
    if (!Number.isFinite(scalar) || scalar === 0) {
      throw new TypeError(`Money.div scalar must be a finite non-zero number, got ${scalar}`);
    }
    return new _Money(bankersRound(this.amount / scalar), this.currency);
  }
  neg() {
    return new _Money(-this.amount, this.currency);
  }
  isZero() {
    return this.amount === 0;
  }
  isNegative() {
    return this.amount < 0;
  }
  isPositive() {
    return this.amount > 0;
  }
  lt(other) {
    this.assertSameCurrency(other, "lt");
    return this.amount < other.amount;
  }
  lte(other) {
    this.assertSameCurrency(other, "lte");
    return this.amount <= other.amount;
  }
  gt(other) {
    this.assertSameCurrency(other, "gt");
    return this.amount > other.amount;
  }
  gte(other) {
    this.assertSameCurrency(other, "gte");
    return this.amount >= other.amount;
  }
  eq(other) {
    return this.currency === other.currency && this.amount === other.amount;
  }
  /**
   * Pick the larger of two Money values (same currency).
   */
  static max(a, b) {
    a.assertSameCurrency(b, "max");
    return a.amount >= b.amount ? a : b;
  }
  static min(a, b) {
    a.assertSameCurrency(b, "min");
    return a.amount <= b.amount ? a : b;
  }
  toString() {
    const sign = this.amount < 0 ? "-" : "";
    const abs = Math.abs(this.amount);
    const major = Math.floor(abs / 100);
    const minor = abs % 100;
    const symbol = this.currency === "GBP" ? "\xA3" : "$";
    return `${sign}${symbol}${major.toLocaleString("en-US")}.${minor.toString().padStart(2, "0")}`;
  }
  toJSON() {
    return { amount: this.amount, currency: this.currency };
  }
  assertSameCurrency(other, op) {
    if (this.currency !== other.currency) {
      throw new TypeError(`Money.${op}: currency mismatch (${this.currency} vs ${other.currency})`);
    }
  }
};
function bankersRound(n) {
  const floor = Math.floor(n);
  const diff = n - floor;
  if (diff < 0.5) return floor;
  if (diff > 0.5) return floor + 1;
  return floor % 2 === 0 ? floor : floor + 1;
}

// src/year.ts
function year(n) {
  if (!Number.isInteger(n)) {
    throw new TypeError(`Year must be an integer, got ${n}`);
  }
  if (n < 2e3 || n > 2100) {
    throw new RangeError(`Year out of range: must be in [2000, 2100], got ${n}`);
  }
  return n;
}

// src/pipeline.ts
function runPipeline(scenario, region) {
  if (scenario.regionId !== region.id) {
    throw new Error(
      `runPipeline: scenario.regionId=${scenario.regionId} does not match region.id=${region.id}`
    );
  }
  if (scenario.grossAnnual.currency !== region.currency) {
    throw new Error(
      `runPipeline: scenario currency ${scenario.grossAnnual.currency} does not match region currency ${region.currency}`
    );
  }
  const gross = scenario.grossAnnual;
  const deductions = [];
  let taxableBase = gross;
  for (const step of region.pipeline(scenario.year)) {
    const result = step(taxableBase, scenario, deductions);
    deductions.push(result);
    taxableBase = result.taxableBaseAfter;
  }
  let net = gross;
  for (const d of deductions) net = net.sub(d.amount);
  const annualFixedCosts = scenario.fixedCostsMonthly ? scenario.fixedCostsMonthly.mul(12) : Money.zero(gross.currency);
  const spendable = net.sub(annualFixedCosts);
  const marginalRate = estimateMarginalRate(scenario, region);
  return { scenario, gross, deductions, net, spendable, marginalRate };
}
function estimateMarginalRate(scenario, region) {
  const epsilon = new Money(1e4, scenario.grossAnnual.currency);
  const bumped = {
    ...scenario,
    grossAnnual: scenario.grossAnnual.add(epsilon)
  };
  const baseNet = computeNet(scenario, region).amount;
  const bumpedNet = computeNet(bumped, region).amount;
  const netDelta = bumpedNet - baseNet;
  const grossDelta = epsilon.amount;
  if (grossDelta === 0) return 0;
  return 1 - netDelta / grossDelta;
}
function computeNet(scenario, region) {
  const gross = scenario.grossAnnual;
  const deductions = [];
  let taxableBase = gross;
  for (const step of region.pipeline(scenario.year)) {
    const result = step(taxableBase, scenario, deductions);
    deductions.push(result);
    taxableBase = result.taxableBaseAfter;
  }
  let net = gross;
  for (const d of deductions) net = net.sub(d.amount);
  return net;
}
function reducingDeduction(name, amount) {
  return (taxableBase) => ({
    name,
    amount,
    taxableBaseAfter: taxableBase.sub(amount)
  });
}
function nonReducingDeduction(name, amount) {
  return (taxableBase) => ({
    name,
    amount,
    taxableBaseAfter: taxableBase
  });
}

// src/bands.ts
function applyBands(taxable, schedule, currency) {
  if (taxable.currency !== currency) {
    throw new TypeError(`applyBands: currency mismatch (${taxable.currency} vs ${currency})`);
  }
  const amount = taxable.amount;
  if (amount <= 0) {
    return { total: Money.zero(currency), bandBreakdown: [] };
  }
  let due = 0;
  const breakdown = [];
  for (const band of schedule.bands) {
    if (amount <= band.from) break;
    const top = Math.min(amount, band.to);
    const portion = top - band.from;
    if (portion <= 0) continue;
    const taxOnBand = portion * band.rate;
    due += taxOnBand;
    breakdown.push({
      from: band.from,
      to: band.to,
      rate: band.rate,
      taxable: portion,
      tax: taxOnBand
    });
    if (amount <= band.to) break;
  }
  if (schedule.modifiers) {
    for (const modifier of schedule.modifiers) {
      due = modifier(due, amount);
    }
  }
  const rounded = Math.round(due);
  return { total: new Money(rounded, currency), bandBreakdown: breakdown };
}
function marginalRateAt(taxableBase, schedules) {
  let rate = 0;
  for (const schedule of schedules) {
    const band = schedule.bands.find((b) => taxableBase >= b.from && taxableBase < b.to);
    if (band) rate += band.rate;
  }
  return rate;
}

// src/regions/uk/bands/income-tax-2026-27.ts
var PERSONAL_ALLOWANCE = 1257e3;
var TAPER_START = 1e7;
function paTaper(amountDue, taxableBase) {
  if (taxableBase <= TAPER_START) return amountDue;
  const excess = taxableBase - TAPER_START;
  const paReduction = Math.min(PERSONAL_ALLOWANCE, Math.floor(excess / 2));
  return amountDue + paReduction * 0.2;
}
var ukIncomeTax2026_27 = {
  source: "https://www.gov.uk/income-tax-rates",
  effectiveFrom: "2026-04-06",
  bands: [
    { from: 0, to: PERSONAL_ALLOWANCE, rate: 0 },
    { from: PERSONAL_ALLOWANCE, to: 5027e3, rate: 0.2 },
    { from: 5027e3, to: 12514e3, rate: 0.4 },
    { from: 12514e3, to: Infinity, rate: 0.45 }
  ],
  modifiers: [paTaper]
};

// src/regions/uk/deductions/income-tax.ts
function incomeTax(year2) {
  const schedule = getSchedule(year2);
  return (taxableBase) => {
    const { total, bandBreakdown } = applyBands(taxableBase, schedule, taxableBase.currency);
    return {
      name: "income-tax",
      amount: total,
      taxableBaseAfter: taxableBase,
      meta: { bandBreakdown, scheduleSource: schedule.source }
    };
  };
}
function getSchedule(year2) {
  if (year2 === 2026) return ukIncomeTax2026_27;
  throw new Error(`uk-eng income tax: no band schedule for year ${year2}`);
}

// src/regions/uk/bands/ni-class-1-2026-27.ts
var ukNiClass1_2026_27 = {
  source: "https://www.gov.uk/national-insurance-rates-letters",
  effectiveFrom: "2026-04-06",
  bands: [
    { from: 0, to: 1257e3, rate: 0 },
    { from: 1257e3, to: 5027e3, rate: 0.08 },
    { from: 5027e3, to: Infinity, rate: 0.02 }
  ]
};

// src/regions/uk/deductions/ni-class-1.ts
function niClass1(year2) {
  const schedule = getSchedule2(year2);
  return (taxableBase) => {
    const { total, bandBreakdown } = applyBands(taxableBase, schedule, taxableBase.currency);
    return {
      name: "ni-class-1",
      amount: total,
      taxableBaseAfter: taxableBase,
      meta: { bandBreakdown, scheduleSource: schedule.source }
    };
  };
}
function getSchedule2(year2) {
  if (year2 === 2026) return ukNiClass1_2026_27;
  throw new Error(`uk-eng NI Class 1: no band schedule for year ${year2}`);
}

// src/regions/uk/deductions/salary-sacrifice-pension.ts
var salarySacrificePension = (taxableBase, ctx) => {
  const currency = taxableBase.currency;
  const pct = ctx.pensionPct ?? 0;
  if (pct <= 0) {
    return {
      name: "salary-sacrifice-pension",
      amount: Money.zero(currency),
      taxableBaseAfter: taxableBase,
      meta: { pensionPct: 0 }
    };
  }
  if (pct > 100) {
    throw new RangeError(`pensionPct must be in [0, 100], got ${pct}`);
  }
  const pence = Math.round(ctx.grossAnnual.amount * pct / 100);
  const amount = new Money(pence, currency);
  return {
    name: "salary-sacrifice-pension",
    amount,
    taxableBaseAfter: taxableBase.sub(amount),
    meta: { pensionPct: pct }
  };
};

// src/regions/uk/bands/student-loan-plan-1-2026-27.ts
var ukStudentLoanPlan1_2026_27 = {
  source: "https://www.gov.uk/repaying-your-student-loan/what-you-pay",
  effectiveFrom: "2026-04-06",
  bands: [
    { from: 0, to: 2606500, rate: 0 },
    { from: 2606500, to: Infinity, rate: 0.09 }
  ]
};

// src/regions/uk/bands/student-loan-plan-2-2026-27.ts
var ukStudentLoanPlan2_2026_27 = {
  source: "https://www.gov.uk/repaying-your-student-loan/what-you-pay",
  effectiveFrom: "2026-04-06",
  bands: [
    { from: 0, to: 2847e3, rate: 0 },
    { from: 2847e3, to: Infinity, rate: 0.09 }
  ]
};

// src/regions/uk/bands/student-loan-plan-4-2026-27.ts
var ukStudentLoanPlan4_2026_27 = {
  source: "https://www.gov.uk/repaying-your-student-loan/what-you-pay",
  effectiveFrom: "2026-04-06",
  bands: [
    { from: 0, to: 3274500, rate: 0 },
    { from: 3274500, to: Infinity, rate: 0.09 }
  ]
};

// src/regions/uk/bands/student-loan-plan-5-2026-27.ts
var ukStudentLoanPlan5_2026_27 = {
  source: "https://www.gov.uk/repaying-your-student-loan/what-you-pay",
  effectiveFrom: "2026-04-06",
  bands: [
    { from: 0, to: 25e5, rate: 0 },
    { from: 25e5, to: Infinity, rate: 0.09 }
  ]
};

// src/regions/uk/bands/student-loan-postgrad-2026-27.ts
var ukStudentLoanPostgrad_2026_27 = {
  source: "https://www.gov.uk/repaying-your-student-loan/what-you-pay",
  effectiveFrom: "2026-04-06",
  bands: [
    { from: 0, to: 21e5, rate: 0 },
    { from: 21e5, to: Infinity, rate: 0.06 }
  ]
};

// src/regions/uk/deductions/student-loan.ts
function studentLoan(year2) {
  return (taxableBase, ctx) => {
    const currency = taxableBase.currency;
    if (!ctx.studentLoan) {
      return {
        name: "student-loan-none",
        amount: Money.zero(currency),
        taxableBaseAfter: taxableBase
      };
    }
    const schedule = getSchedule3(year2, ctx.studentLoan.plan);
    if (!schedule) {
      return {
        name: `student-loan-${ctx.studentLoan.plan}`,
        amount: Money.zero(currency),
        taxableBaseAfter: taxableBase,
        meta: { note: "plan not implemented in v1" }
      };
    }
    const { total, bandBreakdown } = applyBands(ctx.grossAnnual, schedule, currency);
    return {
      name: `student-loan-${ctx.studentLoan.plan}`,
      amount: total,
      taxableBaseAfter: taxableBase,
      meta: { bandBreakdown, scheduleSource: schedule.source }
    };
  };
}
function getSchedule3(year2, plan) {
  if (year2 !== 2026) {
    throw new Error(`uk-eng student loan: no band schedule for year ${year2}`);
  }
  switch (plan) {
    case "uk-plan-1":
      return ukStudentLoanPlan1_2026_27;
    case "uk-plan-2":
      return ukStudentLoanPlan2_2026_27;
    case "uk-plan-4":
      return ukStudentLoanPlan4_2026_27;
    case "uk-plan-5":
      return ukStudentLoanPlan5_2026_27;
    case "uk-postgrad":
      return ukStudentLoanPostgrad_2026_27;
    case "us-federal-ibr":
      return void 0;
  }
}

// src/regions/uk/eng.ts
var ukEngland = {
  id: "uk-eng",
  label: "UK (England, Wales, NI)",
  currency: "GBP",
  pipeline: (year2) => [salarySacrificePension, incomeTax(year2), niClass1(year2), studentLoan(year2)]
};

// src/regions/us/federal/deductions/401k.ts
var US_401K_ELECTIVE_DEFERRAL_LIMIT_2026 = 235e4;
var us401kPretax = (taxableBase, ctx) => {
  const pct = ctx.pensionPct ?? 0;
  const gross = ctx.grossAnnual;
  if (pct <= 0) {
    return {
      name: "401k-pretax",
      amount: Money.zero(gross.currency),
      taxableBaseAfter: taxableBase,
      meta: { pensionPct: 0 }
    };
  }
  if (pct > 100) {
    throw new RangeError(`pensionPct must be in [0, 100], got ${pct}`);
  }
  const uncapped = gross.mul(pct / 100);
  const cap = new Money(US_401K_ELECTIVE_DEFERRAL_LIMIT_2026, gross.currency);
  const contribution = Money.min(uncapped, cap);
  return {
    name: "401k-pretax",
    amount: contribution,
    taxableBaseAfter: taxableBase.sub(contribution),
    meta: { pensionPct: pct, cap: cap.amount }
  };
};

// src/regions/us/federal/bands/income-tax-single-2026.ts
var usFederalIncomeTaxSingle2026 = {
  source: "https://www.irs.gov/forms-pubs/about-publication-17",
  effectiveFrom: "2026-01-01",
  bands: [
    { from: 0, to: 1192500, rate: 0.1 },
    { from: 1192500, to: 4847500, rate: 0.12 },
    { from: 4847500, to: 10335e3, rate: 0.22 },
    { from: 10335e3, to: 1973e4, rate: 0.24 },
    { from: 1973e4, to: 25052500, rate: 0.32 },
    { from: 25052500, to: 62635e3, rate: 0.35 },
    { from: 62635e3, to: Infinity, rate: 0.37 }
  ]
};

// src/regions/us/federal/standard-deduction-2026.ts
var usFederalStandardDeduction2026 = new Money(15e5, "USD");

// src/regions/us/federal/deductions/income-tax.ts
function usFederalIncomeTax(year2) {
  if (year2 !== 2026) {
    throw new Error(`usFederalIncomeTax: year ${year2} not supported (only 2026)`);
  }
  const schedule = usFederalIncomeTaxSingle2026;
  const standardDeduction = usFederalStandardDeduction2026;
  return (taxableBase) => {
    const taxableIncome = Money.max(taxableBase.sub(standardDeduction), Money.zero("USD"));
    const { total, bandBreakdown } = applyBands(taxableIncome, schedule, "USD");
    return {
      name: "federal-income-tax",
      amount: total,
      taxableBaseAfter: taxableBase,
      meta: {
        taxableIncome: taxableIncome.amount,
        standardDeduction: standardDeduction.amount,
        bandBreakdown
      }
    };
  };
}

// src/regions/us/federal/bands/additional-medicare-2026.ts
var ADDITIONAL_MEDICARE_THRESHOLD_SINGLE = 2e7;
var usAdditionalMedicare2026 = {
  source: "https://www.irs.gov/businesses/small-businesses-self-employed/questions-and-answers-for-the-additional-medicare-tax",
  effectiveFrom: "2013-01-01",
  bands: [
    { from: 0, to: ADDITIONAL_MEDICARE_THRESHOLD_SINGLE, rate: 0 },
    { from: ADDITIONAL_MEDICARE_THRESHOLD_SINGLE, to: Infinity, rate: 9e-3 }
  ]
};

// src/regions/us/federal/bands/medicare-2026.ts
var usMedicare2026 = {
  source: "https://www.irs.gov/taxtopics/tc751",
  effectiveFrom: "2026-01-01",
  bands: [{ from: 0, to: Infinity, rate: 0.0145 }]
};

// src/regions/us/federal/bands/social-security-2026.ts
var SOCIAL_SECURITY_WAGE_BASE_2026 = 1749e4;
var usSocialSecurity2026 = {
  source: "https://www.ssa.gov/oact/cola/cbb.html",
  effectiveFrom: "2026-01-01",
  bands: [
    { from: 0, to: SOCIAL_SECURITY_WAGE_BASE_2026, rate: 0.062 },
    { from: SOCIAL_SECURITY_WAGE_BASE_2026, to: Infinity, rate: 0 }
  ]
};

// src/regions/us/federal/deductions/fica.ts
function usFICA(year2) {
  if (year2 !== 2026) {
    throw new Error(`usFICA: year ${year2} not supported (only 2026)`);
  }
  const ssSchedule = usSocialSecurity2026;
  const medSchedule = usMedicare2026;
  const addMedSchedule = usAdditionalMedicare2026;
  return (taxableBase, ctx) => {
    const gross = ctx.grossAnnual;
    const ss = applyBands(gross, ssSchedule, "USD").total;
    const med = applyBands(gross, medSchedule, "USD").total;
    const addMed = applyBands(gross, addMedSchedule, "USD").total;
    const total = ss.add(med).add(addMed);
    return {
      name: "fica",
      amount: total,
      taxableBaseAfter: taxableBase,
      meta: {
        socialSecurity: ss.amount,
        medicare: med.amount,
        additionalMedicare: addMed.amount
      }
    };
  };
}

// src/regions/us/federal/deductions/student-loan.ts
function usFederalStudentLoan(_year) {
  return (taxableBase) => ({
    name: "federal-student-loan",
    amount: Money.zero("USD"),
    taxableBaseAfter: taxableBase,
    meta: { stub: true, deferredTo: "v1.1" }
  });
}

// src/regions/us/states/ca/bands/income-tax-single-2026.ts
var MHST_THRESHOLD = 1e8;
function mentalHealthServicesTax(amountDue, taxableBase) {
  if (taxableBase <= MHST_THRESHOLD) return amountDue;
  const excess = taxableBase - MHST_THRESHOLD;
  return amountDue + excess * 0.01;
}
var caIncomeTaxSingle2026 = {
  source: "https://www.ftb.ca.gov/file/personal/tax-calculator-tables-rates.html",
  effectiveFrom: "2026-01-01",
  bands: [
    { from: 0, to: 1075600, rate: 0.01 },
    { from: 1075600, to: 2549900, rate: 0.02 },
    { from: 2549900, to: 4024500, rate: 0.04 },
    { from: 4024500, to: 5586600, rate: 0.06 },
    { from: 5586600, to: 7060600, rate: 0.08 },
    { from: 7060600, to: 36065900, rate: 0.093 },
    { from: 36065900, to: 43278700, rate: 0.103 },
    { from: 43278700, to: 72131400, rate: 0.113 },
    { from: 72131400, to: Infinity, rate: 0.123 }
  ],
  modifiers: [mentalHealthServicesTax]
};

// src/regions/us/states/ca/deductions/income-tax.ts
var caStandardDeductionSingle2026 = new Money(55e4, "USD");
function caStateIncomeTax(year2) {
  if (year2 !== 2026) {
    throw new Error(`caStateIncomeTax: year ${year2} not supported (only 2026)`);
  }
  const schedule = caIncomeTaxSingle2026;
  const standardDeduction = caStandardDeductionSingle2026;
  return (taxableBase) => {
    const taxableIncome = Money.max(taxableBase.sub(standardDeduction), Money.zero("USD"));
    const { total, bandBreakdown } = applyBands(taxableIncome, schedule, "USD");
    return {
      name: "ca-state-income-tax",
      amount: total,
      taxableBaseAfter: taxableBase,
      meta: {
        taxableIncome: taxableIncome.amount,
        standardDeduction: standardDeduction.amount,
        bandBreakdown
      }
    };
  };
}

// src/regions/us/states/ca/bands/sdi-2026.ts
var caSdi2026 = {
  source: "https://edd.ca.gov/en/disability/sdi_calculations/",
  effectiveFrom: "2026-01-01",
  bands: [{ from: 0, to: Infinity, rate: 0.011 }]
};

// src/regions/us/states/ca/deductions/sdi.ts
function caSDI(year2) {
  if (year2 !== 2026) {
    throw new Error(`caSDI: year ${year2} not supported (only 2026)`);
  }
  const schedule = caSdi2026;
  return (taxableBase, ctx) => {
    const gross = ctx.grossAnnual;
    const { total } = applyBands(gross, schedule, "USD");
    return {
      name: "ca-sdi",
      amount: total,
      taxableBaseAfter: taxableBase
    };
  };
}

// src/regions/us/ca.ts
var usCalifornia = {
  id: "us-ca",
  label: "US \u2014 California",
  currency: "USD",
  pipeline: (year2) => [
    us401kPretax,
    usFederalIncomeTax(year2),
    caStateIncomeTax(year2),
    usFICA(year2),
    caSDI(year2),
    usFederalStudentLoan(year2)
  ]
};

// src/regions/us/states/ny/bands/income-tax-single-2026.ts
var nyIncomeTaxSingle2026 = {
  source: "https://www.tax.ny.gov/pit/file/tax_tables.htm",
  effectiveFrom: "2026-01-01",
  bands: [
    { from: 0, to: 85e4, rate: 0.04 },
    { from: 85e4, to: 117e4, rate: 0.045 },
    { from: 117e4, to: 139e4, rate: 0.0525 },
    { from: 139e4, to: 8065e3, rate: 0.055 },
    { from: 8065e3, to: 2154e4, rate: 0.06 },
    { from: 2154e4, to: 107755e3, rate: 0.0685 },
    { from: 107755e3, to: 5e8, rate: 0.0965 },
    { from: 5e8, to: 25e8, rate: 0.103 },
    { from: 25e8, to: Infinity, rate: 0.109 }
  ]
};

// src/regions/us/states/ny/deductions/income-tax.ts
var nyStandardDeductionSingle2026 = new Money(8e5, "USD");
function nyStateIncomeTax(year2) {
  if (year2 !== 2026) {
    throw new Error(`nyStateIncomeTax: year ${year2} not supported (only 2026)`);
  }
  const schedule = nyIncomeTaxSingle2026;
  const standardDeduction = nyStandardDeductionSingle2026;
  return (taxableBase) => {
    const taxableIncome = Money.max(taxableBase.sub(standardDeduction), Money.zero("USD"));
    const { total, bandBreakdown } = applyBands(taxableIncome, schedule, "USD");
    return {
      name: "ny-state-income-tax",
      amount: total,
      taxableBaseAfter: taxableBase,
      meta: {
        taxableIncome: taxableIncome.amount,
        standardDeduction: standardDeduction.amount,
        bandBreakdown
      }
    };
  };
}

// src/regions/us/states/ny/deductions/sdi.ts
var NY_SDI_ANNUAL_MAX = 3120;
function nySDI(_year) {
  return (taxableBase, ctx) => {
    const amount = ctx.grossAnnual.isPositive() ? new Money(NY_SDI_ANNUAL_MAX, "USD") : Money.zero("USD");
    return {
      name: "ny-sdi",
      amount,
      taxableBaseAfter: taxableBase,
      meta: { cappedAnnual: NY_SDI_ANNUAL_MAX }
    };
  };
}

// src/regions/us/ny.ts
var usNewYork = {
  id: "us-ny",
  label: "US \u2014 New York",
  currency: "USD",
  pipeline: (year2) => [
    us401kPretax,
    usFederalIncomeTax(year2),
    nyStateIncomeTax(year2),
    usFICA(year2),
    nySDI(year2),
    usFederalStudentLoan(year2)
  ]
};

// src/regions/us/tx.ts
var usTexas = {
  id: "us-tx",
  label: "US \u2014 Texas",
  currency: "USD",
  pipeline: (year2) => [
    us401kPretax,
    usFederalIncomeTax(year2),
    usFICA(year2),
    usFederalStudentLoan(year2)
  ]
};

// src/regions/us/wa.ts
var usWashington = {
  id: "us-wa",
  label: "US \u2014 Washington",
  currency: "USD",
  pipeline: (year2) => [
    us401kPretax,
    usFederalIncomeTax(year2),
    usFICA(year2),
    usFederalStudentLoan(year2)
  ]
};

// src/regions/index.ts
var REGIONS = {
  "uk-eng": ukEngland,
  "us-ca": usCalifornia,
  "us-ny": usNewYork,
  "us-wa": usWashington,
  "us-tx": usTexas
};
function getRegion(id) {
  const region = REGIONS[id];
  if (!region) {
    throw new Error(`Unknown region: ${id}`);
  }
  return region;
}
function listRegions() {
  return Object.keys(REGIONS).map((id) => {
    const region = REGIONS[id];
    return { id: region.id, label: region.label, currency: region.currency };
  });
}

// src/calculate.ts
function calculate(scenario) {
  const region = getRegion(scenario.regionId);
  return runPipeline(scenario, region);
}
function sweep(base, range) {
  if (range.from.currency !== range.to.currency || range.from.currency !== range.step.currency) {
    throw new TypeError("sweep: from/to/step must share currency");
  }
  if (range.step.amount <= 0) {
    throw new RangeError("sweep: step must be positive");
  }
  if (range.from.amount > range.to.amount) {
    throw new RangeError("sweep: from must be \u2264 to");
  }
  const results = [];
  let g = range.from.amount;
  while (g <= range.to.amount) {
    const scenario = {
      ...base,
      grossAnnual: new Money(g, range.from.currency)
    };
    results.push(calculate(scenario));
    g += range.step.amount;
  }
  return results;
}
function listYears(regionId) {
  return getRegion(regionId).pipeline.length > 0 ? Array.from(SUPPORTED_YEARS[regionId] ?? []) : [];
}
var SUPPORTED_YEARS = {
  "uk-eng": [2026],
  "us-ca": [2026],
  "us-ny": [2026],
  "us-wa": [2026],
  "us-tx": [2026]
};
export {
  Money,
  SUPPORTED_YEARS,
  applyBands,
  bankersRound,
  calculate,
  listRegions,
  listYears,
  marginalRateAt,
  nonReducingDeduction,
  reducingDeduction,
  runPipeline,
  sweep,
  year
};
